import { parsePhoneNumber, isValidPhoneNumber, CountryCode } from 'libphonenumber-js';

export interface PhoneValidationResult {
  isValid: boolean;
  formattedNumber?: string;
  country?: string;
  error?: string;
}

export interface CountryDetectionResult {
  country: string;
  countryCode: CountryCode;
}

// Country detection based on IP geolocation
export async function detectUserCountry(): Promise<CountryDetectionResult> {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    const countryCode = data.country_code?.toUpperCase() as CountryCode;
    const country = data.country_name;
    
    if (!countryCode || !country) {
      // Default to US if detection fails
      return { country: 'United States', countryCode: 'US' };
    }
    
    return { country, countryCode };
  } catch (error) {
    console.warn('Failed to detect user country:', error);
    // Default to US if detection fails
    return { country: 'United States', countryCode: 'US' };
  }
}

// Client-side phone validation using libphonenumber-js
export function validatePhoneNumber(
  phoneNumber: string, 
  countryCode?: CountryCode
): PhoneValidationResult {
  try {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return { isValid: false, error: 'Phone number is required' };
    }

    // Use detected country if provided, otherwise attempt parsing without country context
    const isValid = countryCode 
      ? isValidPhoneNumber(phoneNumber, countryCode)
      : isValidPhoneNumber(phoneNumber);

    if (!isValid) {
      return { isValid: false, error: 'Please enter a valid phone number' };
    }

    const parsedNumber = countryCode 
      ? parsePhoneNumber(phoneNumber, countryCode)
      : parsePhoneNumber(phoneNumber);

    if (!parsedNumber) {
      return { isValid: false, error: 'Could not parse phone number' };
    }

    return {
      isValid: true,
      formattedNumber: parsedNumber.formatInternational(),
      country: parsedNumber.country
    };
  } catch (error) {
    console.error('Phone validation error:', error);
    return { isValid: false, error: 'Invalid phone number format' };
  }
}

// Server-side Twilio validation (to be called from API route)
export async function validatePhoneWithTwilio(
  phoneNumber: string,
  countryCode?: string
): Promise<PhoneValidationResult> {
  try {
    const response = await fetch('/api/validate-phone', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phoneNumber, countryCode }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { isValid: false, error: errorData.error || 'Phone validation failed' };
    }

    return await response.json();
  } catch (error) {
    console.error('Twilio validation error:', error);
    return { isValid: false, error: 'Phone validation service unavailable' };
  }
}
